import React, { useEffect, useState } from 'react';
import { buildApiUrl } from '../config/api';
import type { Item } from '../pages/Marketplace';

type Deal = {
  id: string | number | null;
  name: string;
  price?: string | null;
  originalPrice?: string | null;
  discountPercent?: number | null;
  imageUrl?: string | null;
  url?: string | null;
  source?: string;
  rating?: number | null;
};

type FlashSaleGridProps = {
  searchResults: Item[];
  hasSearched: boolean;
  searchLoading: boolean;
  searchError: string | null;
};

const FlashSaleGrid: React.FC<FlashSaleGridProps> = ({ searchResults, hasSearched, searchLoading, searchError }) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoadingDeals(true);
      try {
        const res = await fetch(`${buildApiUrl('websearch.php')}`);
        const json = await res.json();
        if (json.success) setDeals(json.data);
      } catch (e) {
        // ignore
      } finally {
        setLoadingDeals(false);
      }
    };
    load();
  }, []);

  const renderItems = (items: { id: string | number | null; name: string; imageUrl?: string | null; url?: string | null; price?: string | null; rating?: number | null; originalPrice?: string | null; discountPercent?: number | null; source?: string | null; }[]) => (
    <ul>
      {items.map((d) => (
        <li key={String(d.id) + String(d.url)}>
          <div style={{ marginBottom: '12px' }}>
            {d.imageUrl ? (
              <img src={d.imageUrl} alt={d.name} style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '10px' }} />
            ) : (
              <div className="img-placeholder" aria-label="No image" />
            )}
          </div>
          <h3>
            {d.url ? (
              <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: '#ff6b35', textDecoration: 'none' }}>
                {d.name}
              </a>
            ) : (
              d.name
            )}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
            {d.price && <p style={{ margin: 0 }}><strong>Price:</strong> {d.price}</p>}
            {d.originalPrice && (
              <p style={{ margin: 0, color: '#999', textDecoration: 'line-through' }}>{d.originalPrice}</p>
            )}
            {typeof d.discountPercent === 'number' && (
              <span style={{ background: '#ffedd5', color: '#c2410c', padding: '4px 8px', borderRadius: 8, fontWeight: 600 }}>
                -{d.discountPercent}%
              </span>
            )}
            {typeof d.rating === 'number' && (
              <div className="rating-stars" aria-label={`Rating ${d.rating}`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className={i < Math.round(d.rating || 0) ? 'star filled' : 'star'}>★</span>
                ))}
              </div>
            )}
          </div>
          {d.source && (
            <p style={{ marginTop: 6, color: '#999', fontSize: '0.85rem' }}>Source: <span style={{ color: '#ff6b35', fontWeight: 600 }}>{d.source}</span></p>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <div style={{ marginTop: '1.5rem' }}>
      {hasSearched && (
        <section style={{ marginBottom: '2rem' }}>
          <h2 className="results-title">Search Results</h2>
          {searchLoading ? (
            <p style={{ textAlign: 'center', color: '#666' }}>Searching...</p>
          ) : searchError ? (
            <p style={{ textAlign: 'center', color: '#e53e3e' }}>{searchError}</p>
          ) : searchResults.length > 0 ? (
            renderItems(searchResults)
          ) : (
            <p style={{ textAlign: 'center', color: '#666' }}>No items found. Try a different keyword!</p>
          )}
        </section>
      )}

      <section>
        <h2 className="results-title">Today&apos;s Flash Deals</h2>
        {loadingDeals ? (
          <p style={{ textAlign: 'center', color: '#666' }}>Loading today&apos;s flash deals...</p>
        ) : deals.length > 0 ? (
          renderItems(deals)
        ) : (
          <p style={{ textAlign: 'center', color: '#666' }}>No flash deals available at the moment.</p>
        )}
      </section>
    </div>
  );
};

export default FlashSaleGrid;


